<script setup>
import { computed, ref, watch } from 'vue';

import { useAlert } from 'dashboard/composables';
import KraftchatAIAPI from 'dashboard/api/kraftchatAI';

import KraftchatAIPlaygroundComposer from './KraftchatAIPlaygroundComposer.vue';
import KraftchatAIPlaygroundResult from './KraftchatAIPlaygroundResult.vue';

import { playgroundContent } from '../../helpers/kraftchatAIPlaygroundConfig';

const props = defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
});

const selectedAgentId = ref('');
const question = ref(playgroundContent.defaultQuestion);
const result = ref(null);
const isTesting = ref(false);

const hasAgents = computed(() => props.agents.length > 0);

const applyDefaultAgent = () => {
  if (!selectedAgentId.value && props.agents.length) {
    selectedAgentId.value = String(props.agents[0].id);
  }
};

const handleSubmit = async () => {
  if (!selectedAgentId.value) {
    useAlert('Configure um agente IA antes de testar.');
    return;
  }

  if (!question.value.trim()) {
    useAlert('Digite uma pergunta para testar.');
    return;
  }

  isTesting.value = true;
  result.value = null;

  try {
    const response = await KraftchatAIAPI.simulatePlayground({
      ai_agent_id: Number(selectedAgentId.value),
      question: question.value.trim(),
    });

    result.value = response.data.payload;
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.message ||
        'Não foi possível testar a resposta da IA.'
    );
  } finally {
    isTesting.value = false;
  }
};

watch(
  () => props.agents,
  () => applyDefaultAgent(),
  { immediate: true }
);
</script>

<template>
  <section class="rounded-xl border border-n-weak bg-n-solid-1 p-6 shadow-sm">
    <div class="max-w-5xl space-y-6">
      <div class="space-y-1">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ playgroundContent.title }}
        </h2>

        <p class="text-sm leading-6 text-n-slate-11">
          {{ playgroundContent.description }}
        </p>
      </div>

      <div
        v-if="!hasAgents"
        class="rounded-xl border border-n-weak bg-n-background p-5"
      >
        <p class="text-sm font-medium text-n-slate-12">
          {{ playgroundContent.emptyAgentTitle }}
        </p>

        <p class="mt-1 text-sm leading-6 text-n-slate-11">
          {{ playgroundContent.emptyAgentDescription }}
        </p>
      </div>

      <template v-else>
        <KraftchatAIPlaygroundComposer
          v-model:selected-agent-id="selectedAgentId"
          v-model:question="question"
          :agents="agents"
          :is-testing="isTesting"
          @submit="handleSubmit"
        />

        <KraftchatAIPlaygroundResult :result="result" />
      </template>
    </div>
  </section>
</template>
